// src/pages/rules.js
import React, { useState, useEffect, useMemo } from "react";
import LoadingIndicator from "../components/LoadingIndicator";
import { FaPlus, FaTimes, FaChevronDown, FaGavel, FaUser, FaUsers, FaEye, FaEyeSlash, FaMoneyBillWave, FaClock, FaCalendarMinus, FaUserSecret, FaLock } from "react-icons/fa";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useCompany } from "../contexts/CompanyContext";
import { useAuth } from "../contexts/AuthContext";

const boxStyle = {
  position: "relative",
  display: "inline-block",
  flex: "0 1 220px",
  minWidth: 150,
  maxWidth: 260,
};

const inputStyle = {
  width: "100%",
  height: 50,
  padding: "8px 34px 8px 12px",
  border: "1px solid #d1d5db",
  borderRadius: 3,
  background: "#ffffff",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle = {
  position: "absolute",
  top: -10,
  left: 12,
  fontSize: "0.7rem",
  background: "#ffffff",
  padding: "0 4px",
  color: "#6b7280",
  userSelect: "none",
};

const toolbarButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 42,
  height: 42,
  borderRadius: 8,
  color: "white",
  fontSize: 18,
  padding: 0,
  boxSizing: "border-box",
  cursor: "pointer",
};

const RULE_TYPES = {
  absence: "Frånvaroregler",
  overtime: "Övertidsregler",
  break: "Rastregler",
  general: "Allmänna regler",
};

const RULE_TYPE_OPTIONS = [
  { value: "general", label: "Allmänna regler" },
  { value: "absence", label: "Frånvaroregler" },
  { value: "overtime", label: "Övertidsregler" },
  { value: "break", label: "Rastregler" },
];

const STATUS_LABELS_SV = {
  active: "Aktiv",
  pending: "Inväntar",
  expired: "Utgången",
  invited: "Inbjuden",
  disabled: "Inaktiverad",
};

const ROLE_LABELS_SV = {
  "super-admin": "Super-admin",
  admin: "Admin",
  user: "Användare",
};

export default function Rules() {
  const { activeCompany, loading: companyLoading } = useCompany();
  const { currentUser } = useAuth();
  
  const [rules, setRules] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [savingError, setSavingError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("employees");
  const [updatingEmployee, setUpdatingEmployee] = useState(null);
  const [showVisibilityConfirm, setShowVisibilityConfirm] = useState(false);
  const [pendingEmployee, setPendingEmployee] = useState(null);
  const [pendingFeature, setPendingFeature] = useState(null);
  
  const [newRule, setNewRule] = useState({
    name: "",
    type: "general",
    description: "",
    value: "",
    unit: "",
    isActive: true,
    appliesToAll: true,
    employeeIds: [],
  });

  // Hämta regler baserat på aktivt företag
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;

    if (!accountToUse) {
      setRules([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const rulesRef = collection(db, "users", accountToUse, "rules");

    const unsubscribe = onSnapshot(
      rulesRef,
      (snapshot) => {
        const rulesList = [];

        snapshot.forEach((ruleDoc) => {
          const ruleData = ruleDoc.data();
          rulesList.push({
            id: ruleDoc.id,
            ...ruleData,
            createdAt: ruleData.createdAt?.toDate
              ? ruleData.createdAt.toDate()
              : ruleData.createdAt
              ? new Date(ruleData.createdAt)
              : null,
            updatedAt: ruleData.updatedAt?.toDate
              ? ruleData.updatedAt.toDate()
              : ruleData.updatedAt
              ? new Date(ruleData.updatedAt)
              : null,
          });
        });

        const sortedRules = rulesList.sort((a, b) => {
          if (a.type === b.type) {
            return (a.name || "").localeCompare(b.name || "");
          }
          return (a.type || "").localeCompare(b.type || "");
        });

        setRules(sortedRules);
        setLoading(false);
      },
      (error) => {
        console.error("Fel vid realtidshämtning av regler:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [activeCompany?.companyAccountUid]);

  // Hämta alla anställda för företaget (realtid)
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;

    if (!accountToUse) {
      setEmployees([]);
      setEmployeesLoading(false);
      return;
    }

    const employedRef = collection(db, "users", accountToUse, "employed");

    const unsubscribe = onSnapshot(
      employedRef,
      (snapshot) => {
        const users = [];

        snapshot.forEach((userDoc) => {
          const userData = userDoc.data();

          let fullName = userData.name || "";
          if (userData.firstName && userData.lastName) {
            fullName = `${userData.firstName} ${userData.lastName}`;
          } else if (userData.firstName) {
            fullName = userData.firstName;
          } else if (userData.lastName) {
            fullName = userData.lastName;
          } else if (userData.displayName) {
            fullName = userData.displayName;
          }

          users.push({
            id: userDoc.id,
            ...userData,
            name: fullName || "Användare",
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
            email: userData.email || "E-post saknas",
            role: userData.role || "user",
            membershipStatus: userData.membershipStatus || userData.status || "invited",
            hiddenPayroll: userData.hiddenPayroll === true,
            hiddenOvertime: userData.hiddenOvertime === true,
            hiddenAbsence: userData.hiddenAbsence === true,
            showOnlyOwnShifts: userData.showOnlyOwnShifts === true,
            authUid: userData.authUid || null,
            createdAt: userData.createdAt?.toDate
              ? userData.createdAt.toDate()
              : userData.createdAt
              ? new Date(userData.createdAt)
              : null,
          });
        });

        const sortedUsers = users.sort((a, b) => {
          const roleOrder = {
            "super-admin": 1,
            admin: 2,
            user: 3,
          };
          const orderA = roleOrder[a.role] || 99;
          const orderB = roleOrder[b.role] || 99;
          if (orderA === orderB) {
            return (a.name || "").localeCompare(b.name || "");
          }
          return orderA - orderB;
        });

        setEmployees(sortedUsers);
        setEmployeesLoading(false);
      },
      (error) => {
        console.error("Fel vid hämtning av anställda:", error);
        setEmployeesLoading(false);
      }
    );

    return () => unsubscribe();
  }, [activeCompany?.companyAccountUid]);

  const canChangeEmployeeFeatures = (employee) => {
    if (employee.role !== "super-admin" && employee.role !== "superadmin") return true;

    const isSuperAdmin = ["super-admin", "superadmin"].includes(activeCompany?.role);
    const isOwnAccount = employee.authUid
      ? employee.authUid === currentUser?.uid
      : Boolean(currentUser?.email && employee.email?.trim().toLowerCase() === currentUser.email.trim().toLowerCase());

    return isSuperAdmin && isOwnAccount;
  };

  // Funktion för att visa/dölja specifik feature för anställd
  const confirmToggleFeature = (employee, feature) => {
    if (!canChangeEmployeeFeatures(employee)) return;
    setPendingEmployee(employee);
    setPendingFeature(feature);
    setShowVisibilityConfirm(true);
  };

  const executeToggleFeature = async () => {
    if (!pendingEmployee || !pendingFeature) return;

    const currentEmployee = employees.find((employee) => employee.id === pendingEmployee.id);
    if (!currentEmployee || !canChangeEmployeeFeatures(currentEmployee)) {
      setShowVisibilityConfirm(false);
      setPendingEmployee(null);
      setPendingFeature(null);
      return;
    }
    
    const accountToUse = activeCompany?.companyAccountUid;
    if (!accountToUse) return;

    setUpdatingEmployee(pendingEmployee.id);
    
    try {
      let updateField = {};
      let newState = false;
      let featureName = "";
      
      if (pendingFeature === 'payroll') {
        newState = !pendingEmployee.hiddenPayroll;
        updateField = { hiddenPayroll: newState };
        featureName = "lönefliken";
      } else if (pendingFeature === 'overtime') {
        newState = !pendingEmployee.hiddenOvertime;
        updateField = { hiddenOvertime: newState };
        featureName = "övertidsansökningar";
      } else if (pendingFeature === 'absence') {
        newState = !pendingEmployee.hiddenAbsence;
        updateField = { hiddenAbsence: newState };
        featureName = "frånvaroansökningar";
      } else if (pendingFeature === 'showOnlyOwn') {
        newState = !pendingEmployee.showOnlyOwnShifts;
        updateField = { showOnlyOwnShifts: newState };
        featureName = "visa endast egna pass";
      }
      
      const employeeRef = doc(db, "users", accountToUse, "employed", pendingEmployee.id);
      await updateDoc(employeeRef, {
        ...updateField,
        updatedAt: serverTimestamp(),
      });
      
      if (pendingEmployee.authUid) {
        const userRef = doc(db, "users", pendingEmployee.authUid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          await updateDoc(userRef, {
            ...updateField,
            updatedAt: serverTimestamp(),
          });
        }
      }
      
      console.log(`Anställd ${pendingEmployee.name} - ${featureName} ${newState ? 'aktiverad' : 'avaktiverad'}`);
      
    } catch (error) {
      console.error("Fel vid uppdatering av anställd:", error);
      alert("Kunde inte uppdatera anställd. Försök igen.");
    } finally {
      setUpdatingEmployee(null);
      setShowVisibilityConfirm(false);
      setPendingEmployee(null);
      setPendingFeature(null);
    }
  };

  // Filtrera regler
  const filteredRules = useMemo(() => {
    return rules.filter((rule) => {
      const searchMatch =
        !searchTerm ||
        (rule.name && rule.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (rule.description && rule.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (rule.value && rule.value.toLowerCase().includes(searchTerm.toLowerCase()));

      const typeMatch = typeFilter === "all" || rule.type === typeFilter;

      return searchMatch && typeMatch;
    });
  }, [rules, searchTerm, typeFilter]);

  // Filtrera anställda
  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      const searchMatch =
        !employeeSearchTerm ||
        (emp.name && emp.name.toLowerCase().includes(employeeSearchTerm.toLowerCase())) ||
        (emp.email && emp.email.toLowerCase().includes(employeeSearchTerm.toLowerCase())) ||
        (ROLE_LABELS_SV[emp.role] && ROLE_LABELS_SV[emp.role].toLowerCase().includes(employeeSearchTerm.toLowerCase()));

      return searchMatch;
    });
  }, [employees, employeeSearchTerm]);

  const formatDate = (date) => {
    if (!date) return "Okänt datum";
    return date.toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleEditClick = (rule) => {
    setSelectedRule(rule);
    setShowEditPopup(true);
  };

  const handleSaveRule = async () => {
    const accountToUse = activeCompany?.companyAccountUid;

    if (!selectedRule || !accountToUse) return;

    try {
      setIsSubmitting(true);

      const updateData = {
        name: selectedRule.name || "",
        type: selectedRule.type || "general",
        description: selectedRule.description || "",
        value: selectedRule.value || "",
        unit: selectedRule.unit || "",
        isActive: selectedRule.isActive ?? true,
        appliesToAll: selectedRule.appliesToAll ?? true,
        employeeIds: selectedRule.employeeIds || [],
        updatedAt: serverTimestamp(),
      };

      await updateDoc(
        doc(db, "users", accountToUse, "rules", selectedRule.id),
        updateData
      );

      setShowEditPopup(false);
      setSelectedRule(null);
    } catch (error) {
      console.error("Fel vid uppdatering av regel:", error);
      setSavingError("Det gick inte att uppdatera regeln. Försök igen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRule = async () => {
    const accountToUse = activeCompany?.companyAccountUid;

    if (!selectedRule || !accountToUse) return;

    try {
      setIsSubmitting(true);

      await deleteDoc(
        doc(db, "users", accountToUse, "rules", selectedRule.id)
      );

      setShowDeleteConfirm(false);
      setShowEditPopup(false);
      setSelectedRule(null);
    } catch (error) {
      console.error("Fel vid borttagning av regel:", error);
      setSavingError("Det gick inte att ta bort regeln. Försök igen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewRuleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewRule((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setSavingError("");
  };

  const handleEmployeeSelection = (employeeId) => {
    setNewRule((prev) => {
      const currentIds = prev.employeeIds;
      let newIds;
      
      if (currentIds.includes(employeeId)) {
        newIds = currentIds.filter(id => id !== employeeId);
      } else {
        newIds = [...currentIds, employeeId];
      }
      
      return {
        ...prev,
        employeeIds: newIds,
        appliesToAll: newIds.length === 0,
      };
    });
  };

  const handleSaveNewRule = async () => {
    try {
      setSavingError("");

      if (!newRule.name || !newRule.name.trim()) {
        setSavingError("Regelns namn måste fyllas i");
        return;
      }

      const accountToUse = activeCompany?.companyAccountUid;

      if (!accountToUse) {
        setSavingError("Inget företagskonto hittades.");
        return;
      }

      setIsSubmitting(true);

      const rulesRef = collection(db, "users", accountToUse, "rules");

      await addDoc(rulesRef, {
        name: newRule.name.trim(),
        type: newRule.type,
        description: newRule.description?.trim() || "",
        value: newRule.value?.trim() || "",
        unit: newRule.unit?.trim() || "",
        isActive: newRule.isActive ?? true,
        appliesToAll: newRule.appliesToAll ?? true,
        employeeIds: newRule.employeeIds || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: accountToUse,
      });

      setShowAddPopup(false);
      setNewRule({
        name: "",
        type: "general",
        description: "",
        value: "",
        unit: "",
        isActive: true,
        appliesToAll: true,
        employeeIds: [],
      });

      alert(`Regeln "${newRule.name}" har skapats!`);
    } catch (error) {
      console.error("Fel vid skapande av regel:", error);
      setSavingError(error.message || "Det gick inte att skapa regeln.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRuleTypeLabel = (type) => {
    return RULE_TYPES[type] || type || "Okänd typ";
  };

  const getRuleTypeColor = (type) => {
    switch (type) {
      case "absence":
        return "#f59e0b";
      case "overtime":
        return "#3b82f6";
      case "break":
        return "#8b5cf6";
      default:
        return "#10b981";
    }
  };

  const getEmployeeNames = (employeeIds) => {
    if (!employeeIds || employeeIds.length === 0) return "Alla anställda";
    const names = employeeIds.slice(0, 3).map(id => {
      const emp = employees.find(e => e.id === id);
      return emp ? emp.name : id;
    });
    if (employeeIds.length > 3) {
      return `${names.join(", ")} +${employeeIds.length - 3} fler`;
    }
    return names.join(", ");
  };

  // Render feature button - med blå färg för begränsade funktioner (veckovy)
  const renderFeatureButton = (employee, feature, isActive, isRestricted) => {
    const isLocked = !canChangeEmployeeFeatures(employee);
    let buttonText = "";
    let buttonIcon = null;
    let buttonColor = "";
    
    // Bestäm utseende baserat på feature och state
    if (feature === 'payroll') {
      if (isRestricted) {
        buttonText = "Dold";
        buttonIcon = <FaEyeSlash />;
        buttonColor = "#9ca3af"; // Grå när begränsad
      } else {
        buttonText = "Synlig";
        buttonIcon = <FaEye />;
        buttonColor = "#10b981"; // Grön när aktiv
      }
    } 
    else if (feature === 'overtime') {
      if (isRestricted) {
        buttonText = "Dold";
        buttonIcon = <FaEyeSlash />;
        buttonColor = "#9ca3af"; // Grå när begränsad
      } else {
        buttonText = "Synlig";
        buttonIcon = <FaEye />;
        buttonColor = "#10b981"; // Grön när aktiv
      }
    }
    else if (feature === 'absence') {
      if (isRestricted) {
        buttonText = "Dold";
        buttonIcon = <FaEyeSlash />;
        buttonColor = "#9ca3af"; // Grå när begränsad
      } else {
        buttonText = "Synlig";
        buttonIcon = <FaEye />;
        buttonColor = "#10b981"; // Grön när aktiv
      }
    }
    else if (feature === 'showOnlyOwn') {
      if (isRestricted) {
        buttonText = "Egna pass";
        buttonIcon = <FaUserSecret />;
        buttonColor = "#3b82f6"; // BLÅ för begränsad vy (veckovy)
      } else {
        buttonText = "Alla pass";
        buttonIcon = <FaUsers />;
        buttonColor = "#10b981"; // Grön för fri vy
      }
    }
    
    return (
      <button
        onClick={() => confirmToggleFeature(employee, feature)}
        disabled={isLocked || updatingEmployee === employee.id}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 14px",
          borderRadius: 8,
          border: "none",
          background: buttonColor,
          color: "#fff",
          fontWeight: 600,
          cursor: isLocked ? "not-allowed" : updatingEmployee === employee.id ? "wait" : "pointer",
          fontSize: 13,
          opacity: isLocked ? 0.6 : updatingEmployee === employee.id ? 0.7 : 1,
          transition: "all 0.2s",
          width: "100%",
          justifyContent: "center",
          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
        }}
        title={isLocked ? "Endast super-admin kan ändra sina egna begränsningar" : isRestricted ? `Aktivera ${feature === 'payroll' ? 'lön' : feature === 'overtime' ? 'övertid' : feature === 'absence' ? 'frånvaro' : 'visning av alla pass'}` : `Inaktivera ${feature === 'payroll' ? 'lön' : feature === 'overtime' ? 'övertid' : feature === 'absence' ? 'frånvaro' : 'visning av alla pass'}`}
      >
        {updatingEmployee === employee.id && pendingFeature === feature ? (
          "Uppdaterar..."
        ) : (
          <>
            {buttonIcon}
            {buttonText}
          </>
        )}
      </button>
    );
  };

  // Visa laddningsindikator
  if (companyLoading || loading || employeesLoading) {
    return <LoadingIndicator message="Laddar regler..." fullPage />;
  }

  if (!activeCompany) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>
        Inget företag valt. Vänligen logga in igen.
      </div>
    );
  }

  return (
    <div style={{ padding: "0 0 24px" }}>
      {/* Visa aktivt företag */}
      <div style={{ marginBottom: 16, padding: "0 0 8px" }}>
        <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
          Visar data för: <strong>{activeCompany.companyName}</strong>
        </p>
      </div>

      {/* ANSTÄLLDA TAB - Direct view without tabs */}
      <>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 0 20px",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              ...boxStyle,
              minWidth: 320,
              maxWidth: 420,
              flex: "1 1 360px",
            }}
          >
            <input
              type="text"
              value={employeeSearchTerm}
              onChange={(e) => setEmployeeSearchTerm(e.target.value)}
              placeholder="🔍 Sök på namn, e-post eller roll..."
              style={inputStyle}
            />
          </div>
          
          {/* Status filter indicator */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#10b981" }}></span>
              Alla funktioner aktiva
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#f59e0b" }}></span>
              Vissa funktioner begränsade
            </span>
          </div>
        </div>

        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            overflow: "auto",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          {/* Tabell header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1.2fr",
              gap: 12,
              padding: "14px 20px",
              borderBottom: "2px solid #e5e7eb",
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
              minWidth: 1000,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              <FaUser style={{ marginRight: 6, fontSize: 11 }} />
              NAMN
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center" }}>
              <FaMoneyBillWave style={{ marginRight: 6, fontSize: 11 }} />
              LÖN
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center" }}>
              <FaClock style={{ marginRight: 6, fontSize: 11 }} />
              ÖVERTID
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center" }}>
              <FaCalendarMinus style={{ marginRight: 6, fontSize: 11 }} />
              FRÅNVARO
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center" }}>
              <FaUserSecret style={{ marginRight: 6, fontSize: 11 }} />
              PASS-VY
            </div>
          </div>

          {employees.length === 0 ? (
            <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>
              <FaUsers size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
              <p>Inga anställda hittades.</p>
            </div>
          ) : filteredEmployees.length > 0 ? (
            filteredEmployees.map((emp) => {
              const isPayrollHidden = emp.hiddenPayroll === true;
              const isOvertimeHidden = emp.hiddenOvertime === true;
              const isAbsenceHidden = emp.hiddenAbsence === true;
              const showOnlyOwnShifts = emp.showOnlyOwnShifts === true;
              const isActive = emp.membershipStatus === "active";
              const hasRestrictions = isPayrollHidden || isOvertimeHidden || isAbsenceHidden || showOnlyOwnShifts;
              
              let rowBgColor = "#ffffff";
              let statusColor = "";
              
              if (hasRestrictions) {
                rowBgColor = "#fffbeb";
                statusColor = "#f59e0b";
              } else if (isActive) {
                rowBgColor = "#f0fdf4";
                statusColor = "#10b981";
              } else {
                statusColor = "#94a3b8";
              }

              return (
                <div
                  key={emp.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1.2fr",
                    gap: 12,
                    alignItems: "center",
                    padding: "16px 20px",
                    borderBottom: "1px solid #f1f5f9",
                    background: rowBgColor,
                    transition: "all 0.2s",
                    minWidth: 1000,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fefce8";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = rowBgColor;
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, ${statusColor}20, ${statusColor}40)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: statusColor,
                      fontWeight: 600
                    }}>
                      {emp.name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: "#1e293b" }}>
                        {emp.name}
                        {["super-admin", "superadmin"].includes(emp.role) && (
                          <FaLock title="Endast super-admin kan ändra sina egna begränsningar" aria-label="Skyddad super-admin" style={{ color: "#64748b", fontSize: 12 }} />
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                        {ROLE_LABELS_SV[emp.role] || emp.role}
                      </div>
                    </div>
                    {hasRestrictions && (
                      <div style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: statusColor,
                        background: `${statusColor}15`,
                        padding: "2px 8px",
                        borderRadius: 12,
                        whiteSpace: "nowrap"
                      }}>
                        Begränsad
                      </div>
                    )}
                  </div>

                  {/* LÖN - Grön om synlig, Grå om dold */}
                  <div>{renderFeatureButton(emp, 'payroll', !isPayrollHidden, isPayrollHidden)}</div>
                  
                  {/* ÖVERTID - Grön om synlig, Grå om dold */}
                  <div>{renderFeatureButton(emp, 'overtime', !isOvertimeHidden, isOvertimeHidden)}</div>
                  
                  {/* FRÅNVARO - Grön om synlig, Grå om dold */}
                  <div>{renderFeatureButton(emp, 'absence', !isAbsenceHidden, isAbsenceHidden)}</div>
                  
                  {/* PASS-VY - Grön för alla pass, BLÅ för endast egna */}
                  <div>{renderFeatureButton(emp, 'showOnlyOwn', !showOnlyOwnShifts, showOnlyOwnShifts)}</div>
                </div>
              );
            })
          ) : (
            <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>
              <FaUser size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
              <p>Inga anställda hittades med sökningen</p>
              <button
                onClick={() => setEmployeeSearchTerm("")}
                style={{
                  marginTop: 16,
                  padding: "8px 16px",
                  background: "#3b82f6",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 13
                }}
              >
                Rensa sökning
              </button>
            </div>
          )}
        </div>
        
        {/* Bekräftelsedialog för visa/dölj */}
        {showVisibilityConfirm && pendingEmployee && pendingFeature && (
          <div
            onClick={() => {
              setShowVisibilityConfirm(false);
              setPendingEmployee(null);
              setPendingFeature(null);
            }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 5000,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                borderRadius: 16,
                padding: 24,
                width: 450,
                maxWidth: "90vw",
                boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                border: "1px solid #e5e7eb",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                {pendingFeature === 'payroll' ? (
                  pendingEmployee.hiddenPayroll ? (
                    <FaEye style={{ fontSize: 28, color: "#10b981" }} />
                  ) : (
                    <FaEyeSlash style={{ fontSize: 28, color: "#9ca3af" }} />
                  )
                ) : pendingFeature === 'overtime' ? (
                  pendingEmployee.hiddenOvertime ? (
                    <FaEye style={{ fontSize: 28, color: "#10b981" }} />
                  ) : (
                    <FaEyeSlash style={{ fontSize: 28, color: "#9ca3af" }} />
                  )
                ) : pendingFeature === 'absence' ? (
                  pendingEmployee.hiddenAbsence ? (
                    <FaEye style={{ fontSize: 28, color: "#10b981" }} />
                  ) : (
                    <FaEyeSlash style={{ fontSize: 28, color: "#9ca3af" }} />
                  )
                ) : (
                  pendingEmployee.showOnlyOwnShifts ? (
                    <FaUsers style={{ fontSize: 28, color: "#10b981" }} />
                  ) : (
                    <FaUserSecret style={{ fontSize: 28, color: "#3b82f6" }} /> // BLÅ FÄRG för begränsad veckovy
                  )
                )}
                <h3 style={{ margin: 0, fontSize: 20, color: "#111827" }}>
                  {pendingFeature === 'payroll' 
                    ? (pendingEmployee.hiddenPayroll ? "Visa löneflik" : "Dölj löneflik")
                    : pendingFeature === 'overtime'
                    ? (pendingEmployee.hiddenOvertime ? "Visa övertidsansökningar" : "Dölj övertidsansökningar")
                    : pendingFeature === 'absence'
                    ? (pendingEmployee.hiddenAbsence ? "Visa frånvaroansökningar" : "Dölj frånvaroansökningar")
                    : (pendingEmployee.showOnlyOwnShifts ? "Visa alla anställdas pass" : "Visa endast egna pass")
                  }
                </h3>
              </div>

              <p style={{ margin: "0 0 12px", color: "#4b5563", lineHeight: 1.5 }}>
                <strong>{pendingEmployee.name}</strong>
              </p>

              {pendingFeature === 'payroll' ? (
                pendingEmployee.hiddenPayroll ? (
                  <div style={{ background: "#f0fdf4", padding: 12, borderRadius: 8, margin: "12px 0", borderLeft: "3px solid #10b981" }}>
                    <p style={{ margin: 0, fontSize: 14, color: "#166534" }}>
                      ✅ När du <strong>visar</strong> lönefliken kommer användaren att se "Min lön" i mobilappen.
                    </p>
                  </div>
                ) : (
                  <div style={{ background: "#f3f4f6", padding: 12, borderRadius: 8, margin: "12px 0", borderLeft: "3px solid #9ca3af" }}>
                    <p style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600, color: "#374151" }}>
                      ⚠️ Detta kommer att dölja lönefliken <strong>"Min lön"</strong> för användaren.
                    </p>
                  </div>
                )
              ) : pendingFeature === 'overtime' ? (
                pendingEmployee.hiddenOvertime ? (
                  <div style={{ background: "#f0fdf4", padding: 12, borderRadius: 8, margin: "12px 0", borderLeft: "3px solid #10b981" }}>
                    <p style={{ margin: 0, fontSize: 14, color: "#166534" }}>
                      ✅ När du <strong>visar</strong> övertidsansökningar kommer användaren att kunna skapa övertidsansökningar.
                    </p>
                  </div>
                ) : (
                  <div style={{ background: "#f3f4f6", padding: 12, borderRadius: 8, margin: "12px 0", borderLeft: "3px solid #9ca3af" }}>
                    <p style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600, color: "#374151" }}>
                      ⚠️ Detta kommer att dölja möjligheten att skapa <strong>övertidsansökningar</strong> för användaren.
                    </p>
                  </div>
                )
              ) : pendingFeature === 'absence' ? (
                pendingEmployee.hiddenAbsence ? (
                  <div style={{ background: "#f0fdf4", padding: 12, borderRadius: 8, margin: "12px 0", borderLeft: "3px solid #10b981" }}>
                    <p style={{ margin: 0, fontSize: 14, color: "#166534" }}>
                      ✅ När du <strong>visar</strong> frånvaroansökningar kommer användaren att kunna skapa frånvaroansökningar.
                    </p>
                  </div>
                ) : (
                  <div style={{ background: "#f3f4f6", padding: 12, borderRadius: 8, margin: "12px 0", borderLeft: "3px solid #9ca3af" }}>
                    <p style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600, color: "#374151" }}>
                      ⚠️ Detta kommer att dölja möjligheten att skapa <strong>frånvaroansökningar</strong> för användaren.
                    </p>
                  </div>
                )
              ) : (
                pendingEmployee.showOnlyOwnShifts ? (
                  <div style={{ background: "#f0fdf4", padding: 12, borderRadius: 8, margin: "12px 0", borderLeft: "3px solid #10b981" }}>
                    <p style={{ margin: 0, fontSize: 14, color: "#166534" }}>
                      ✅ När du <strong>visar alla</strong> kommer användaren att se alla anställdas pass i veckovyn.
                    </p>
                  </div>
                ) : (
                  <div style={{ background: "#eff6ff", padding: 12, borderRadius: 8, margin: "12px 0", borderLeft: "3px solid #3b82f6" }}>
                    <p style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600, color: "#1e40af" }}>
                      🔒 Detta kommer att begränsa användaren till att <strong>endast se sina egna pass</strong> i veckovyn.
                    </p>
                    <p style={{ margin: "8px 0 0 0", fontSize: 13, color: "#1e40af" }}>
                      Andra anställdas pass kommer inte att visas för denna användare.
                    </p>
                  </div>
                )
              )}

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  marginTop: 24,
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={() => {
                    setShowVisibilityConfirm(false);
                    setPendingEmployee(null);
                    setPendingFeature(null);
                  }}
                  style={{
                    height: 42,
                    padding: "0 20px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    color: "#111827",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Avbryt
                </button>
                <button
                  onClick={executeToggleFeature}
                  style={{
                    height: 42,
                    padding: "0 20px",
                    borderRadius: 10,
                    border: "none",
                    background: (pendingFeature === 'payroll'
                      ? (pendingEmployee.hiddenPayroll ? "#10b981" : "#9ca3af")
                      : pendingFeature === 'overtime'
                      ? (pendingEmployee.hiddenOvertime ? "#10b981" : "#9ca3af")
                      : pendingFeature === 'absence'
                      ? (pendingEmployee.hiddenAbsence ? "#10b981" : "#9ca3af")
                      : (pendingEmployee.showOnlyOwnShifts ? "#10b981" : "#3b82f6") // BLÅ FÄRG för begränsad veckovy
                    ),
                    color: "#fff",
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {pendingFeature === 'payroll' 
                    ? (pendingEmployee.hiddenPayroll ? <><FaEye /> Ja, visa lön</> : <><FaEyeSlash /> Ja, dölj lön</>)
                    : pendingFeature === 'overtime'
                    ? (pendingEmployee.hiddenOvertime ? <><FaEye /> Ja, visa övertid</> : <><FaEyeSlash /> Ja, dölj övertid</>)
                    : pendingFeature === 'absence'
                    ? (pendingEmployee.hiddenAbsence ? <><FaEye /> Ja, visa frånvaro</> : <><FaEyeSlash /> Ja, dölj frånvaro</>)
                    : (pendingEmployee.showOnlyOwnShifts ? <><FaUsers /> Ja, visa alla</> : <><FaUserSecret /> Ja, endast egna</>)
                  }
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    </div>
  );
}
